import React, { useState, useMemo, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Typography,
    Box,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Pagination,
    InputAdornment,
    Button,
    Collapse,
    Badge,
    useTheme,
    useMediaQuery,
    Tooltip,
    Chip
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    RotateLeft as ResetIcon,
    FileDownload as DownloadIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Clear as ClearIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useLanguage } from '../contexts/LanguageContext';

export interface ColumnDef {
    id: string;
    label: string;
    minWidth?: number;
    width?: number;
    chip?: boolean;
    chipColorMap?: Record<string, any>;
    format?: (value: any, row?: any) => string | React.ReactNode;
    render?: (row: any, value?: any) => string | React.ReactNode;
}

export type ColumnConfig = ColumnDef;

interface GenericTableProps {
    columns: ColumnDef[];
    data: any[];
    loading?: boolean;
    onEdit?: (row: any) => void;
    onDelete?: (row: any) => void;
    rowActions?: (row: any) => React.ReactNode; // Custom extra action buttons per row
    filterColumns?: string[]; // Columns to show dropdown filters for
    searchPlaceholder?: string; // Placeholder for search text
    maxHeight?: string | number; // Maximum scrollable height for table body
}

const GenericTable: React.FC<GenericTableProps> = ({
    columns,
    data,
    onEdit,
    onDelete,
    rowActions,
    filterColumns,
    searchPlaceholder,
    maxHeight
}) => {
    const { t } = useLanguage();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // Pagination & Filter States
    const [page, setPage] = useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = useState<number>(15);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
    const [showFilters, setShowFilters] = useState<boolean>(!isMobile);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

    // Synchronize showFilters when device screen size changes
    useEffect(() => {
        setShowFilters(!isMobile);
    }, [isMobile]);

    // Handle ESC key to exit fullscreen
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isFullscreen) {
                setIsFullscreen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen]);

    // Count active filters (search query + dropdown selection count)
    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (searchQuery.trim() !== '') count++;
        Object.values(columnFilters).forEach(val => {
            if (val && val !== '') count++;
        });
        return count;
    }, [searchQuery, columnFilters]);

    // 1. Auto-detect filterable columns if not explicitly provided
    const resolvedFilterColumns = useMemo(() => {
        if (filterColumns) return filterColumns;
        if (!data || data.length === 0) return [];
        
        // Auto-detect columns with unique values count between 2 and 12
        const detected: string[] = [];
        columns.forEach(col => {
            // Skip IDs, text-heavy fields, numbers, and dates
            const skipIds = [
                'id', 'registrationNumber', 'firstName', 'lastName', 'phone', 
                'telegramChatId', 'licenseNumber', 'mileage', 'year', 
                'brand', 'model', 'color', 'date', 'assignedDate', 
                'hireDate', 'licenseExpiry', 'expiryDate', 'dateDebut', 'dateFin'
            ];
            if (skipIds.some(skip => col.id.toLowerCase().includes(skip.toLowerCase()))) {
                return;
            }
            
            const uniqueSet = new Set<string>();
            data.forEach(row => {
                const val = row[col.id];
                if (val !== undefined && val !== null && val !== '') {
                    uniqueSet.add(String(val));
                }
            });
            
            if (uniqueSet.size >= 2 && uniqueSet.size <= 12) {
                detected.push(col.id);
            }
        });
        return detected;
    }, [filterColumns, columns, data]);

    // 2. Compute unique values for each resolved filter column
    const uniqueValues = useMemo(() => {
        const values: Record<string, string[]> = {};
        resolvedFilterColumns.forEach(colId => {
            const uniqueSet = new Set<string>();
            data.forEach(row => {
                const val = row[colId];
                if (val !== undefined && val !== null && val !== '') {
                    uniqueSet.add(String(val));
                }
            });
            values[colId] = Array.from(uniqueSet).sort();
        });
        return values;
    }, [resolvedFilterColumns, data]);

    // 3. Filter data based on search query and column dropdown filters
    const filteredData = useMemo(() => {
        if (!data) return [];
        return data.filter(row => {
            // Text search matches
            if (searchQuery) {
                const lowerQuery = searchQuery.toLowerCase();
                const matchesText = columns.some(col => {
                    const val = row[col.id];
                    if (val === undefined || val === null) return false;
                    return String(val).toLowerCase().includes(lowerQuery);
                });
                if (!matchesText) return false;
            }

            // Dropdown column filter matches
            const matchesFilters = Object.entries(columnFilters).every(([colId, filterVal]) => {
                if (!filterVal) return true;
                const val = row[colId];
                return val !== undefined && val !== null && String(val) === filterVal;
            });
            
            return matchesFilters;
        });
    }, [data, searchQuery, columnFilters, columns]);

    // 4. Paginate the filtered data
    const paginatedData = useMemo(() => {
        return filteredData.slice((page - 1) * rowsPerPage, page * rowsPerPage);
    }, [filteredData, page, rowsPerPage]);

    const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

    const handleResetFilters = () => {
        setSearchQuery('');
        setColumnFilters({});
        setPage(1);
    };

    // 5. Excel and CSV export handlers
    const handleExportExcel = () => {
        const rows = filteredData.map(row => {
            const rowData: Record<string, any> = {};
            columns.forEach(col => {
                const val = row[col.id];
                rowData[col.label] = val !== undefined && val !== null ? String(val) : '';
            });
            return rowData;
        });

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Données');
        XLSX.writeFile(workbook, `Export_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const handleExportCSV = () => {
        const headers = columns.map(c => c.label);
        const csvRows = [headers.join(',')];

        filteredData.forEach(row => {
            const values = columns.map(col => {
                const val = row[col.id];
                const cleanVal = val !== undefined && val !== null ? String(val).replace(/"/g, '""') : '';
                return `"${cleanVal}"`;
            });
            csvRows.push(values.join(','));
        });

        const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + csvRows.join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Export_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (!data || data.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc', borderRadius: '12px' }}>
                <Typography variant="body1" color="text.secondary">
                    {t('common.status.noData') || 'Aucune donnée disponible.'}
                </Typography>
            </Box>
        );
    }

    return (
        <Box 
            sx={isFullscreen ? {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1250,
                bgcolor: 'background.default',
                p: { xs: 1.5, sm: 2.5 },
                width: '100vw',
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            } : {
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
        >
            {/* Filter Toolbar Section */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                mb: 2,
                p: 1.5,
                borderRadius: '12px',
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: theme.shadows[1]
            }}>
                {/* Compact Control Bar */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    width: '100%'
                }}>
                    {/* Search Input */}
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder={searchPlaceholder || (t('grid.search.placeholder') !== 'grid.search.placeholder' ? t('grid.search.placeholder') : 'Rechercher...')}
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setPage(1);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null
                        }}
                        sx={{ minWidth: 180, flexGrow: 1, maxWidth: { xs: '100%', sm: 300 } }}
                    />

                    {/* Filter Toggle Button with Badge & Icon */}
                    {resolvedFilterColumns.length > 0 && (
                        <Button
                            size="small"
                            variant={activeFilterCount > 0 ? "contained" : "outlined"}
                            color={activeFilterCount > 0 ? "primary" : "inherit"}
                            startIcon={
                                <Badge badgeContent={activeFilterCount} color="error">
                                    <FilterIcon fontSize="small" />
                                </Badge>
                            }
                            endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            onClick={() => setShowFilters(prev => !prev)}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px',
                                fontWeight: 600,
                                height: '38px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {t('grid.filters.title') !== 'grid.filters.title' ? t('grid.filters.title') : 'Filtres'}
                        </Button>
                    )}

                    {/* Reset Filters Button */}
                    {activeFilterCount > 0 && (
                        <Tooltip title={t('grid.filters.clear') !== 'grid.filters.clear' ? t('grid.filters.clear') : 'Effacer les filtres'}>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={handleResetFilters}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'error.main',
                                    borderRadius: '8px',
                                    height: '38px',
                                    width: '38px',
                                    opacity: 0.85,
                                    '&:hover': { opacity: 1 }
                                }}
                            >
                                <ResetIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* Export Buttons & Controls */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: { xs: 0, sm: 'auto' } }}>
                        <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<DownloadIcon />}
                            onClick={handleExportExcel}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px',
                                fontWeight: 600,
                                height: '36px',
                            }}
                        >
                            Excel
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={<DownloadIcon />}
                            onClick={handleExportCSV}
                            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, height: '36px' }}
                        >
                            CSV
                        </Button>

                        {/* Fullscreen Toggle Button */}
                        <Tooltip title={isFullscreen ? (t('grid.fullscreen.exit') !== 'grid.fullscreen.exit' ? t('grid.fullscreen.exit') : 'Quitter plein écran') : (t('grid.fullscreen.enter') !== 'grid.fullscreen.enter' ? t('grid.fullscreen.enter') : 'Plein écran')}>
                            <IconButton
                                size="small"
                                color={isFullscreen ? "primary" : "default"}
                                onClick={() => setIsFullscreen(prev => !prev)}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: '8px',
                                    height: '36px',
                                    width: '36px',
                                    bgcolor: isFullscreen ? 'action.selected' : 'transparent'
                                }}
                            >
                                {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Item Counter */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', bgcolor: 'action.selected', px: 1.5, py: 0.5, borderRadius: '20px', whiteSpace: 'nowrap' }}>
                            {filteredData.length} {filteredData.length > 1 ? (t('grid.counter.items') !== 'grid.counter.items' ? t('grid.counter.items') : 'éléments') : (t('grid.counter.item') !== 'grid.counter.item' ? t('grid.counter.item') : 'élément')}
                        </Typography>
                    </Box>
                </Box>

                {/* Collapsible Dropdown Filters Section */}
                {resolvedFilterColumns.length > 0 && (
                    <Collapse in={showFilters} timeout="auto" unmountOnExit={false}>
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 1.5,
                            pt: 1.5,
                            mt: 0.5,
                            borderTop: '1px solid',
                            borderColor: 'divider'
                        }}>
                            {resolvedFilterColumns.map(colId => {
                                const column = columns.find(c => c.id === colId);
                                if (!column) return null;
                                const values = uniqueValues[colId] || [];
                                const currentValue = columnFilters[colId] || '';
                                const allLabel = t('grid.filters.all') !== 'grid.filters.all' ? t('grid.filters.all') : 'Tous';

                                return (
                                    <FormControl key={colId} size="small" sx={{ minWidth: 150, flexGrow: { xs: 1, sm: 0 }, maxWidth: { xs: '100%', sm: 220 } }}>
                                        <InputLabel id={`filter-label-${colId}`}>{`${allLabel} ${column.label}`}</InputLabel>
                                        <Select
                                            labelId={`filter-label-${colId}`}
                                            value={currentValue}
                                            label={`${allLabel} ${column.label}`}
                                            onChange={(e) => {
                                                setColumnFilters(prev => ({ ...prev, [colId]: e.target.value as string }));
                                                setPage(1);
                                            }}
                                            MenuProps={{ sx: { zIndex: 1350 } }}
                                        >
                                            <MenuItem value="">
                                                <em>{allLabel}</em>
                                            </MenuItem>
                                            {values.map(val => (
                                                <MenuItem key={val} value={val}>{val}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                );
                            })}
                        </Box>
                    </Collapse>
                )}
            </Box>
            {/* Table Area */}
            <TableContainer 
                component={Paper} 
                elevation={0} 
                variant="outlined" 
                sx={{ 
                    borderRadius: '12px', 
                    flexGrow: 1,
                    minHeight: 0,
                    maxHeight: isFullscreen ? 'calc(100vh - 200px)' : maxHeight,
                    overflow: 'auto',
                    WebkitOverflowScrolling: 'touch'
                }}
            >
                <Table stickyHeader size="small" aria-label="generic table" sx={{ minWidth: 650 }}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    sx={{ 
                                        minWidth: column.minWidth || column.width,
                                        width: column.width,
                                        fontWeight: 700, 
                                        backgroundColor: 'background.default',
                                        color: 'text.secondary',
                                        py: 1,
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                            {(onEdit || onDelete || rowActions) && (
                                <TableCell 
                                    align="right" 
                                    sx={{ 
                                        fontWeight: 700, 
                                        backgroundColor: 'background.default',
                                        color: 'text.secondary',
                                        py: 1,
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    {t('common.actions.actions') || 'Actions'}
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row, index) => (
                                <TableRow hover role="checkbox" tabIndex={-1} key={row.id || index}>
                                    {columns.map((column) => {
                                        const value = row[column.id];
                                        return (
                                            <TableCell key={column.id} sx={{ py: 0.5, fontSize: '0.8rem' }}>
                                                {column.render ? (
                                                    column.render(row, value)
                                                ) : column.format ? (
                                                    column.format(value, row)
                                                ) : column.chip && value ? (
                                                    <Chip
                                                        label={t(String(value), String(value))}
                                                        size="small"
                                                        color={(column.chipColorMap && column.chipColorMap[value]) || 'default'}
                                                        variant="outlined"
                                                        sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                                                    />
                                                ) : (
                                                    value
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                    {(onEdit || onDelete || rowActions) && (
                                        <TableCell align="right" sx={{ py: 0.25 }}>
                                            {rowActions && rowActions(row)}
                                            {onEdit && (
                                                <IconButton color="primary" onClick={() => onEdit(row)} size="small" sx={{ mr: 0.5 }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            {onDelete && (
                                                <IconButton color="error" onClick={() => onDelete(row)} size="small">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length + (onEdit || onDelete || rowActions ? 1 : 0)} align="center" sx={{ py: 6 }}>
                                    <Typography variant="body1" color="text.secondary">
                                        {t('grid.noData')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination Footer */}
            {filteredData.length > 0 && (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                    mt: 2.5,
                    px: 1
                }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {t('grid.pagination.info', 
                            filteredData.length > 0 ? (page - 1) * rowsPerPage + 1 : 0, 
                            Math.min(page * rowsPerPage, filteredData.length), 
                            filteredData.length
                        )}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">{t('grid.pagination.pageSize')}</Typography>
                            <Select
                                size="small"
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value));
                                    setPage(1);
                                }}
                                sx={{ minWidth: 80, height: '36px' }}
                            >
                                <MenuItem value={15}>15</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                            </Select>
                        </Box>

                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, p) => setPage(p)}
                            color="primary"
                            shape="rounded"
                            size="medium"
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    fontWeight: 'bold',
                                    borderRadius: '8px'
                                }
                            }}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default GenericTable;
